import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { CenterDetails } from "../assets";
import { IoClose } from "react-icons/io5";

// Define the red marker icon
const redIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const centers = CenterDetails;

// Component to update the map center
const MapUpdater = ({ center }) => {
  const map = useMap();
  map.setView(center, 12);
  return null;
};

const CentersPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [mapCenter, setMapCenter] = useState([28.5743, 77.3617]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [isListOpen, setIsListOpen] = useState(false);

  const filteredCenters = centers.filter((center) =>
    center.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    const matchingCenter = centers.find((center) =>
      center.name.toLowerCase().includes(term.toLowerCase())
    );

    if (matchingCenter) {
      setMapCenter(matchingCenter.coordinates);
      setSelectedCenter(matchingCenter.id);
    }
  };

  const handleCenterClick = (center) => {
    setMapCenter(center.coordinates);
    setSelectedCenter(center.id);
    setIsListOpen(false);
  };

  return (
    <div className="w-full h-screen relative bg-gray-200 overflow-hidden">
      {/* Full Screen Map */}
      <div className="w-full h-full">
        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          <MapUpdater center={mapCenter} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          />
          {centers.map((center) => (
            <Marker
              key={center.id}
              position={center.coordinates}
              icon={redIcon}
              eventHandlers={{
                click: () => handleCenterClick(center),
              }}
            >
              <Popup>
                <div className='font-poppins'>
                  <h4 className='font-bold text-[16px] mb-2'>{center.name}</h4>
                  <p className='text-[14px] text-[#666666]'>{center.description}</p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Desktop - Top Left Search (stays on map) */}
      <div className='hidden lg:block absolute top-4 left-4 z-[9999] w-80'>
        <div className='bg-white rounded-[8px] shadow-lg overflow-hidden'>
          {/* Search Input */}
          <div className='p-4 border-b border-[#EEE]'>
            <input
              type="text"
              placeholder="Search centers..."
              value={searchTerm}
              onChange={handleSearchChange}
              className='w-full px-4 py-2.5 border-2 border-[#EE645A] rounded-[8px] focus:outline-none focus:border-[#D84530] transition-colors font-poppins text-[14px] placeholder-[#999999]'
            />
          </div>

          {/* Search Results List */}
          <div className='max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#EE645A] scrollbar-track-[#FFF8F6]'>
            {filteredCenters.length > 0 ? (
              <div className='divide-y divide-[#EEE]'>
                {filteredCenters.map((center) => (
                  <button
                    key={center.id}
                    onClick={() => handleCenterClick(center)}
                    className={`w-full p-4 text-left font-poppins transition-all duration-200 hover:bg-[#FFF8F6] ${
                      selectedCenter === center.id ? 'bg-[#FFF8F6]' : ''
                    }`}
                  >
                    <div className='flex items-start gap-3'>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                        selectedCenter === center.id
                          ? 'bg-[#EE645A] text-white'
                          : 'bg-[#F0F0F0] text-[#666666]'
                      }`}>
                        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                          <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z'/>
                        </svg>
                      </div>
                      <div className='flex-1 min-w-0'>
                        <h4 className='font-bold text-[15px] text-[#000000] truncate'>
                          {center.name}
                        </h4>
                        <p className='text-[13px] text-[#666666] line-clamp-2 mt-1'>
                          {center.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className='p-6 text-center'>
                <p className='text-[14px] text-[#999999] font-poppins'>No centers found</p>
              </div>
            )}
          </div>

          {/* Footer Stats */}
          <div className='p-4 border-t border-[#EEE] bg-[#FAFAFA]'>
            <p className='text-[12px] text-[#666666] text-center font-poppins'>
              Showing {filteredCenters.length} of {centers.length} centers
            </p>
          </div>
        </div>
      </div>

      {/* Mobile - Floating Search Button */}
      <button
        onClick={() => setIsListOpen(!isListOpen)}
        className='lg:hidden absolute top-4 left-4 z-[9998] bg-[#EE645A] text-white p-3 rounded-full shadow-lg hover:bg-[#D84530] transition-all'
        aria-label="Toggle search"
      >
        <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
        </svg>
      </button>

      {/* Mobile - Sliding Drawer Overlay */}
      {isListOpen && (
        <>
          {/* Backdrop */}
          <div 
            className='lg:hidden fixed inset-0 bg-black/40 z-[9995] animate-in fade-in duration-200'
            onClick={() => setIsListOpen(false)}
          />

          {/* Sliding Drawer - Bottom to Top */}
          <div className='lg:hidden fixed bottom-0 left-0 right-0 z-[9996] bg-white rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom-full duration-300 max-h-[80vh]'>
            
            {/* Drag Handle */}
            <div className='flex justify-center pt-2 pb-2'>
              <div className='w-12 h-1 bg-[#DDD] rounded-full'></div>
            </div>

            {/* Header */}
            <div className='flex items-center justify-between px-4 py-3 border-b border-[#EEE]'>
              <h3 className='font-bold text-[18px] text-[#000000]'>
                Centers ({filteredCenters.length})
              </h3>
              <button
                onClick={() => setIsListOpen(false)}
                className='p-1 rounded-md hover:bg-[#FFF8F6] transition-colors'
              >
                <IoClose className='w-6 h-6 text-[#666666]' />
              </button>
            </div>

            {/* Search Input */}
            <div className='px-4 py-3 border-b border-[#EEE] bg-[#FAFAFA]'>
              <input
                type="text"
                placeholder="Search centers..."
                value={searchTerm}
                onChange={handleSearchChange}
                autoFocus
                className='w-full px-4 py-2.5 border-2 border-[#EE645A] rounded-[8px] focus:outline-none focus:border-[#D84530] transition-colors font-poppins text-[14px] placeholder-[#999999]'
              />
            </div>

            {/* Search Results List */}
            <div className='overflow-y-auto scrollbar-thin scrollbar-thumb-[#EE645A] scrollbar-track-[#FFF8F6]' style={{ maxHeight: 'calc(80vh - 180px)' }}>
              {filteredCenters.length > 0 ? (
                <div className='divide-y divide-[#EEE]'>
                  {filteredCenters.map((center) => (
                    <button
                      key={center.id}
                      onClick={() => handleCenterClick(center)}
                      className={`w-full p-4 text-left font-poppins transition-all duration-200 hover:bg-[#FFF8F6] active:bg-[#FFE8E3] ${
                        selectedCenter === center.id ? 'bg-[#FFF8F6] border-l-4 border-[#EE645A]' : ''
                      }`}
                    >
                      <div className='flex items-start gap-3'>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                          selectedCenter === center.id
                            ? 'bg-[#EE645A] text-white'
                            : 'bg-[#F0F0F0] text-[#666666]'
                        }`}>
                          <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z'/>
                          </svg>
                        </div>
                        <div className='flex-1 min-w-0'>
                          <h4 className='font-bold text-[15px] text-[#000000] truncate'>
                            {center.name}
                          </h4>
                          <p className='text-[13px] text-[#666666] line-clamp-2 mt-1'>
                            {center.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className='p-6 text-center'>
                  <p className='text-[14px] text-[#999999] font-poppins'>No centers found</p>
                </div>
              )}
            </div>

            {/* Footer Stats */}
            <div className='p-4 border-t border-[#EEE] bg-[#FAFAFA]'>
              <p className='text-[12px] text-[#666666] text-center font-poppins'>
                Showing {filteredCenters.length} of {centers.length} centers
              </p>
            </div>
          </div>
        </>
      )}

      {/* Bottom Right - Location Info Card (appears when center selected) */}
      {selectedCenter && (
        <div className='absolute bottom-6 right-6 z-[9999] max-w-xs w-[calc(100%-3rem)] sm:max-w-xs'>
          {(() => {
            const center = centers.find(c => c.id === selectedCenter);
            return center ? (
              <div className='bg-white rounded-[12px] shadow-xl overflow-hidden'>
                {/* Card Header */}
                <div className='bg-[#EE645A] text-white p-4'>
                  <h3 className='font-bold text-[16px] sm:text-[18px] truncate'>
                    {center.name}
                  </h3>
                </div>

                {/* Card Content */}
                <div className='p-4 space-y-3'>
                  {/* Description */}
                  <div>
                    <p className='text-[13px] sm:text-[14px] text-[#333333] leading-relaxed'>
                      {center.description}
                    </p>
                  </div>

                  {/* Location Info */}
                  <div className='space-y-2 pt-2 border-t border-[#EEE]'>
                    <div className='flex items-start gap-3'>
                      <svg className='w-5 h-5 text-[#EE645A] flex-shrink-0 mt-0.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' />
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' />
                      </svg>
                      <div className='flex-1'>
                        <p className='text-[12px] text-[#666666]'>Coordinates</p>
                        <p className='text-[13px] sm:text-[14px] text-[#333333] font-medium'>
                          {center.coordinates[0].toFixed(4)}, {center.coordinates[1].toFixed(4)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className='pt-2 border-t border-[#EEE] space-y-2'>
                    <button className='w-full py-2 bg-[#EE645A] text-white font-medium text-[13px] sm:text-[14px] rounded-[6px] hover:bg-[#D84530] transition-colors'>
                      Get Directions
                    </button>
                    <button className='w-full py-2 bg-[#FFF8F6] text-[#EE645A] font-medium text-[13px] sm:text-[14px] rounded-[6px] hover:bg-[#FFECE8] transition-colors border border-[#EE645A]'>
                      More Info
                    </button>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Attribution */}
      <div className='absolute top-4 right-4 z-[9999] bg-white px-3 py-1 rounded-[4px] shadow-md'>
        <p className='text-[12px] text-[#666666] font-poppins'>Leaflet Map</p>
      </div>
    </div>
  );
};

export default CentersPage;